import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import Header from "@/components/Header";
import Turnstile from "@/components/Turnstile";
import { useAuth } from "@/hooks/useAuth";
import { verifyTurnstile } from "@/lib/verifyTurnstile";
import { Mail, Lock, ArrowRight } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [resending, setResending] = useState(false);
  const { signIn, resendConfirmation } = useAuth();

  const handleResend = async () => {
    setResending(true);
    const { error } = await resendConfirmation(email);
    setResending(false);
    if (error) {
      toast({ title: "Couldn't resend", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Verification email sent", description: "Check your inbox and spam folder." });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const ok = await verifyTurnstile(captchaToken, "login");
    if (!ok) {
      setLoading(false);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      toast({
        title: "Verification failed",
        description: "Please complete the security check and try again.",
        variant: "destructive",
      });
      return;
    }
    const { error } = await signIn(email, password);
    if (error) {
      setLoading(false);
      setCaptchaToken(null);
      setCaptchaKey((k) => k + 1);
      const msg = error.message?.toLowerCase() ?? "";
      const unconfirmed = msg.includes("not confirmed") || msg.includes("confirm your email");
      setNeedsConfirm(unconfirmed);
      toast({
        title: unconfirmed ? "Email not verified" : "Login failed",
        description: unconfirmed
          ? "Confirm your email first — you can resend the link below."
          : error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Logged in successfully" });
    // Full reload so every component's useAuth picks up the new session from storage
    window.location.assign("/");
  };


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container flex items-center justify-center py-16 md:py-24">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-border bg-card p-8 shadow-[var(--card-shadow)]">
            <div className="text-center mb-8">
              <img src="/logo.png" alt="Vecurate" className="w-20 h-20 object-contain mx-auto mb-4" />
              <h1 className="font-serif text-2xl text-foreground">Welcome Back</h1>
              <p className="text-sm text-muted-foreground mt-1">Sign in to your account</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="pl-10" placeholder="you@example.com" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10" placeholder="••••••••" />
                </div>
              </div>
              <div className="flex justify-center">
                <Turnstile key={captchaKey} action="login" onVerify={setCaptchaToken} onExpire={() => setCaptchaToken(null)} onError={() => setCaptchaToken(null)} />
              </div>
              <Button type="submit" className="w-full gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" disabled={loading || !captchaToken}>
                {loading ? "Verifying..." : "Sign In"}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </form>
            {needsConfirm && (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-center">
                <p className="text-sm text-muted-foreground">
                  Your email isn't verified yet.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 rounded-full"
                  onClick={handleResend}
                  disabled={resending || !email}
                >
                  {resending ? "Sending..." : "Resend verification email"}
                </Button>
              </div>
            )}
            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary hover:underline">
                Forgot your password?
              </Link>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link to="/signup" className="text-primary font-medium hover:underline">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
